import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Loader2, Link2, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STATUS_LABEL = {
  ok: "Synced",
  header_only: "No live posts yet",
  auth_failed: "Sound.me link expired / revoked",
  invalid_config: "Saved link isn't a valid Sound.me CSV URL",
  not_configured: "Add the Sound.me CSV link to enable syncing",
  error: "Sync error",
};

function fmt(ts) {
  if (!ts) return "never";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

export function SoundmeSync({ campaignId, onDone }) {
  const [cfg, setCfg] = useState(null);
  const [campId, setCampId] = useState("");
  const [token, setToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = () =>
    api.get(`/campaigns/${campaignId}/soundme`).then((r) => {
      setCfg(r.data);
      setCampId(r.data.soundme_campaign_id || "");
    }).catch(() => {});
  useEffect(() => { load(); }, [campaignId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/campaigns/${campaignId}/soundme`, {
        soundme_campaign_id: campId.trim(),
        ...(token.trim() ? { soundme_csv_token: token.trim() } : {}),
      });
      setToken("");
      await load();
      toast.success("Sound.me campaign linked");
    } catch { toast.error("Could not save Sound.me settings"); }
    finally { setSaving(false); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/campaigns/${campaignId}/soundme/sync`);
      await load();
      if (data.ok && data.status === "ok") {
        toast.success(`Sound.me: imported ${data.imported}, updated ${data.updated}, skipped ${data.skipped_qc} QC`);
        onDone && onDone();
      } else if (data.status === "header_only") {
        toast.info("Sound.me: no published posts yet (QC/pending are not counted)");
      } else {
        toast.error(`Sound.me: ${STATUS_LABEL[data.status] || data.status}`);
      }
    } catch { toast.error("Sound.me sync failed"); }
    finally { setSyncing(false); }
  };

  if (!cfg) return null;
  const st = cfg.last_status;
  const good = st === "ok" || st === "header_only";
  const r = cfg.last_result;

  return (
    <div data-testid="soundme-panel" className="mb-6 rounded-2xl border border-neutral-200 bg-white p-5">
      <div className="mb-3 flex items-center gap-2">
        <Link2 className="h-4 w-4 text-[#35606E]" />
        <h3 className="font-display text-lg">Sound.me sync</h3>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-xs text-neutral-500">Sound.me Campaign ID</Label>
          <Input data-testid="soundme-campaign-id" value={campId} onChange={(e) => setCampId(e.target.value)}
            placeholder="e.g. 19035" className="mt-1 rounded-lg" />
        </div>
        <div>
          <Label className="text-xs text-neutral-500">
            Statistics CSV link {cfg.soundme_csv_token_set && <span className="text-emerald-600">• configured</span>}
          </Label>
          <Input data-testid="soundme-csv-token" type="password" autoComplete="off" value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder={cfg.soundme_csv_token_set ? "•••• saved — paste a new link to replace" : "paste the Save-as-CSV URL"}
            className="mt-1 rounded-lg" />
          <p className="mt-1 text-[11px] text-neutral-400">Stored securely server-side; never shown again.</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button data-testid="soundme-save-btn" onClick={save} disabled={saving} variant="outline"
          className="rounded-full border-neutral-300 bg-white hover:bg-neutral-50">
          {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Save link
        </Button>
        <Button data-testid="soundme-sync-btn" onClick={sync} disabled={syncing || !cfg.soundme_csv_token_set}
          className="rounded-full bg-[#35606E] hover:bg-[#294b57]">
          <RefreshCw className={`mr-1.5 h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> Refresh from Sound.me
        </Button>
      </div>

      <div data-testid="soundme-status" className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-neutral-600">
        <span className="flex items-center gap-1.5">
          {good ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
          {STATUS_LABEL[st] || (st ? st : "Not synced yet")}
        </span>
        <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> Last sync: {fmt(cfg.last_sync)}</span>
        {r && <span>Imported {r.imported} · Updated {r.updated} · QC skipped {r.skipped_qc}</span>}
      </div>
      {cfg.last_detail && <p className="mt-1 text-xs text-neutral-400">{cfg.last_detail}</p>}
    </div>
  );
}
